import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { site, images } from "@/data/content";
import PageHeader from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { sendContact } from "@/lib/api";

const empty = { name: "", email: "", phone: "", message: "" };

export default function Contacto() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Completa nombre, correo y mensaje.");
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Mensaje enviado. Gracias por escribir.");
      setForm(empty);
    } catch (err) {
      toast.error("No se pudo enviar el mensaje. Inténtalo nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contacto-page">
      <PageHeader
        overline="Contacto"
        titleLines={["Escríbeme"]}
        intro="Si tienes dudas o quieres conversar antes de agendar, este es el lugar."
      />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          {/* Info + image */}
          <div className="md:col-span-5">
            <div className="overflow-hidden aspect-[4/3] mb-10">
              <img
                src={images.room}
                alt="La consulta"
                className="w-full h-full object-cover"
                data-testid="contacto-image"
              />
            </div>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} className="mt-1 text-[#9C5A4B] shrink-0" />
                <div>
                  <p className="overline mb-1">La consulta</p>
                  <p className="text-lg text-[#1a1a1a]/85">{site.location}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Mail size={20} className="mt-1 text-[#9C5A4B] shrink-0" />
                <div>
                  <p className="overline mb-1">Correo</p>
                  <a
                    href={`mailto:${site.email}`}
                    className="text-lg text-[#1a1a1a]/85 link-underline"
                    data-testid="contacto-email-link"
                  >
                    {site.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MessageCircle size={20} className="mt-1 text-[#9C5A4B] shrink-0" />
                <div>
                  <p className="overline mb-1">WhatsApp</p>
                  <a
                    href={`https://wa.me/${site.whatsappRaw}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg text-[#1a1a1a]/85 link-underline"
                    data-testid="contacto-whatsapp-link"
                  >
                    {site.whatsapp}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Form */}
          <div className="md:col-span-6 md:col-start-7">
            <Reveal>
              <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Label htmlFor="c-name" className="overline">Nombre</Label>
                  <Input
                    id="c-name"
                    data-testid="contact-name"
                    value={form.name}
                    onChange={update("name")}
                    className="mt-2 rounded-none border-black/20 bg-transparent h-12 focus-visible:ring-0 focus-visible:border-[#9C5A4B]"
                    placeholder="Tu nombre"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="c-email" className="overline">Correo</Label>
                    <Input
                      id="c-email"
                      type="email"
                      data-testid="contact-email"
                      value={form.email}
                      onChange={update("email")}
                      className="mt-2 rounded-none border-black/20 bg-transparent h-12 focus-visible:ring-0 focus-visible:border-[#9C5A4B]"
                      placeholder="tucorreo@ejemplo.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="overline">Teléfono (opcional)</Label>
                    <Input
                      id="c-phone"
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      className="mt-2 rounded-none border-black/20 bg-transparent h-12 focus-visible:ring-0 focus-visible:border-[#9C5A4B]"
                      placeholder="+56 9 ..."
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-message" className="overline">Mensaje</Label>
                  <Textarea
                    id="c-message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={6}
                    className="mt-2 rounded-none border-black/20 bg-transparent focus-visible:ring-0 focus-visible:border-[#9C5A4B] resize-none"
                    placeholder="Cuéntame brevemente en qué puedo acompañarte."
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="inline-flex items-center gap-3 rounded-full bg-[#9C5A4B] text-[#f9f8f6] px-8 py-4 text-sm tracking-wide hover:bg-[#8A4A3C] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  Enviar mensaje
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
